if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.onSubmitHandler = this.onSubmitHandler.bind(this);
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.form = this.querySelector('form');
        if (!this.form) return;

        const idInput = this.form.querySelector('[name=id]');
        if (idInput) idInput.disabled = false;

        this.form.addEventListener('submit', this.onSubmitHandler);

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        // Main CTA button (for aria-haspopup only)
        this.defaultSubmitButton =
          this.querySelector('.product-form__buttons [type="submit"]') ||
          this.querySelector('[type="submit"]');

        if (this.defaultSubmitButton && document.querySelector('cart-drawer')) {
          this.defaultSubmitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.initStickyCTA();
      }

      disconnectedCallback() {
        if (this.form) {
          this.form.removeEventListener('submit', this.onSubmitHandler);
        }
        if (this.stickyObserver) {
          this.stickyObserver.disconnect();
        }
      }

      initStickyCTA() {
        this.primaryCtaWrapper = this.querySelector('.product-form__buttons');
        this.stickyCtaWrapper = this.querySelector('.product-form__sticky');

        if (!this.primaryCtaWrapper || !this.stickyCtaWrapper) return;

        if (!this.stickyCtaWrapper.innerHTML.trim()) {
          this.stickyCtaWrapper.innerHTML = this.primaryCtaWrapper.innerHTML;
        }

        // ✅ Your real footer
        this.footerEl =
          document.querySelector('.site-footer') ||
          document.querySelector('footer');

        this.primaryInView = false;
        this.footerInView = false;

        const updateSticky = () => {
          if (!this.primaryInView && !this.footerInView) {
            this.stickyCtaWrapper.classList.add('is-visible');
          } else {
            this.stickyCtaWrapper.classList.remove('is-visible');
          }
        };

        this.stickyCtaWrapper.classList.remove('is-visible');

        this.stickyObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.target === this.primaryCtaWrapper) {
                this.primaryInView = entry.isIntersecting;
              }
              if (this.footerEl && entry.target === this.footerEl) {
                this.footerInView = entry.isIntersecting;
              }
            });

            updateSticky();
          },
          {
            root: null,
            threshold: 0,
            // hide a little earlier as footer approaches
            rootMargin: '0px 0px 120px 0px',
          }
        );

        this.stickyObserver.observe(this.primaryCtaWrapper);
        if (this.footerEl) this.stickyObserver.observe(this.footerEl);

        updateSticky();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        // Use the button that actually submitted the form (main OR sticky)
        const submitButton =
          evt.submitter ||
          this.querySelector('.product-form__sticky [type="submit"]') ||
          this.defaultSubmitButton;

        if (!submitButton) return;
        if (submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading');

        // SHOW ALL SPINNERS inside this product-form (main + sticky)
        const spinners = this.querySelectorAll('.loading-overlay__spinner');
        spinners.forEach((spinner) => spinner.classList.remove('hidden'));

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        // Subscription clean-up
        {
          const isSubscription = !!this.form.querySelector(
            '.subscription-widget--item-subscribe input[type="radio"]:checked'
          );
          if (!isSubscription) {
            formData.delete('selling_plan');
          } else {
            const plans = formData
              .getAll('selling_plan')
              .map((v) => String(v).trim());
            const firstValid = plans.find((v) => /^\d+$/.test(v));
            formData.delete('selling_plan');
            if (firstValid) formData.set('selling_plan', firstValid);
          }
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);

              const soldOutMessage =
                submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;

              submitButton.setAttribute('aria-disabled', 'true');
              const textSpan = submitButton.querySelector('span');
              if (textSpan) textSpan.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            }

            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            submitButton.classList.remove('loading');

            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }

            if (!this.error) {
              submitButton.removeAttribute('aria-disabled');
            }

            // HIDE ALL SPINNERS again (main + sticky)
            spinners.forEach((spinner) => spinner.classList.add('hidden'));
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
